import { Observable } from 'rxjs';
import _ from 'lodash';
import type { AnyAction, Store } from 'redux';
import type { ActionsObservable } from 'redux-observable';

import BudgetsApi from 'entities/budget/api';
import BudgetAllocationsApi from 'entities/budgetAllocation/api';
import DonationCreditRulesApi from 'entities/donationCreditRule/api';
import DonationMatchingRulesApi from 'entities/donationMatchingRule/api';
import { selectOrganization, selectUser } from 'entities/user/selectors';
import { STORE as STORE_USER } from 'entities/user/constants';
import { Features } from 'utils/features';
import type { State } from 'reducers';

import { INIT } from './constants';
import { InitAction, initFailedAction, initSuccessAction } from './actions';
import { selectInitialized } from './selectors';

interface StoreUserAction {
  type: string;
}

const init = (
  action$: ActionsObservable<InitAction | StoreUserAction>,
  store: Store<State>
): Observable<AnyAction> =>
  action$.ofType(INIT, STORE_USER).mergeMap(({ type }) => {
    const state = store.getState();
    const organization = selectOrganization(state);
    const user = selectUser(state);

    if (type === STORE_USER && !organization && !selectInitialized(state)) {
      return [];
    }

    const actions: Observable<AnyAction>[] = [BudgetsApi.fetchAll()];

    if (user?.hasFeature(Features.DONATION_CREDIT)) {
      actions.push(DonationCreditRulesApi.fetchAll());
    }

    if (user?.hasFeature(Features.DONATION_MATCHING)) {
      actions.push(DonationMatchingRulesApi.fetchAll());
    }

    if (user?.hasFeature(Features.CORPORATE_DONATIONS)) {
      actions.push(BudgetAllocationsApi.fetchAll());
    }

    if (actions.length === 0) {
      return Observable.of(initSuccessAction());
    }

    return Observable.combineLatest(actions)
      .mergeMap((resultingActions) => [..._.flatten(resultingActions), initSuccessAction()])
      .catch((e) => Observable.of(initFailedAction(e)));
  });

export default [init];
